import { useState } from 'react';
import SubPageHeader from '../SubPageHeader';
import SettingsToggle from '../SettingsToggle';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { prepareOffline } from '@/lib/prepareOffline';
import { cacheClear } from '@/lib/offlineCache';

function usePref(key, def) {
  const [val, setVal] = useState(() => {
    const s = localStorage.getItem(key);
    if (s === null) return def;
    try { return JSON.parse(s); } catch { return s; }
  });
  const set = (v) => { setVal(v); localStorage.setItem(key, JSON.stringify(v)); };
  return [val, set];
}

const CACHE_ITEMS = [
  { key: 'customers', label: 'العملاء', size: '12 MB', icon: '👥' },
  { key: 'products', label: 'المنتجات', size: '45 MB', icon: '📦' },
  { key: 'images', label: 'الصور', size: '230 MB', icon: '🖼️' },
  { key: 'visits', label: 'الزيارات', size: '5 MB', icon: '📍' },
];

export default function SyncDataSection({ onBack }) {
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(localStorage.getItem('last_sync') || null);
  const [progress, setProgress] = useState('');
  const [autoSync, setAutoSync] = usePref('sync_auto', true);
  const [wifiOnly, setWifiOnly] = usePref('sync_wifi_only', true);
  const [interval, setIntervalVal] = usePref('sync_interval', '15');
  const [cleared, setCleared] = useState({});

  const handleSync = async () => {
    if (!navigator.onLine) {
      toast.error('لا يوجد اتصال بالإنترنت');
      return;
    }
    setSyncing(true);
    setProgress('');
    try {
      const { ok } = await prepareOffline((label) => setProgress(label));
      const now = new Date().toISOString();
      localStorage.setItem('last_sync', now);
      setLastSync(now);
      if (ok) toast.success('تم تحضير البيانات للعمل بدون إنترنت ✓');
      else toast.warning('تم التحضير جزئياً — تأكد من الاتصال وأعد المحاولة');
    } catch {
      toast.error('فشل التحضير — تحقق من الإنترنت');
    } finally {
      setSyncing(false);
      setProgress('');
    }
  };

  const clearCache = (key) => {
    setCleared(c => ({ ...c, [key]: true }));
    toast.success(`تم مسح cache ${CACHE_ITEMS.find(i => i.key === key)?.label}`);
  };

  const clearAll = () => {
    const keys = ['cached_customers', 'cached_products', 'cached_images', 'cached_visits'];
    keys.forEach(k => localStorage.removeItem(k));
    cacheClear();
    setCleared({ customers: true, products: true, images: true, visits: true });
    toast.success('تم مسح كل الـ Cache');
  };

  const lastSyncDisplay = lastSync
    ? new Date(lastSync).toLocaleString('ar-EG', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })
    : 'لم تتم مزامنة بعد';

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      <SubPageHeader title="المزامنة والبيانات" onBack={onBack} />
      <div className="p-4 space-y-4">

        {/* Sync Status */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <p className="font-bold text-sm text-emerald-700">حالة المزامنة</p>
          </div>
          <p className="text-xs text-emerald-600">✅ آخر مزامنة: {lastSyncDisplay}</p>
          <p className="text-xs text-emerald-600 mt-0.5">📤 معلق: 0 عمليات</p>
        </div>

        {/* Manual Sync */}
        <div className="bg-card border border-border rounded-2xl p-4">
          <p className="font-bold text-sm text-foreground mb-1">تحضير للعمل بدون إنترنت</p>
          <p className="text-xs text-muted-foreground mb-3">يحمّل كل بياناتك (العملاء، المنتجات، أوامر البيع، التحصيلات، المرتجعات، الهدايا، الزيارات) ويخزّنهم على الجهاز عشان تشتغل حتى من غير شبكة.</p>
          <Button onClick={handleSync} disabled={syncing} className="w-full h-12 rounded-xl gap-2 text-base font-bold">
            {syncing ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري التحضير{progress ? `: ${progress}` : '...'}</>
            ) : '🔄 تحضير الآن'}
          </Button>
        </div>

        {/* Auto Sync Settings */}
        <div className="bg-card border border-border rounded-2xl p-4 space-y-1">
          <p className="font-bold text-sm text-foreground mb-3">إعدادات المزامنة التلقائية</p>

          <div className="flex items-center justify-between py-2.5 border-b border-border">
            <div className="flex items-center gap-2">
              <span className="text-sm">🔄</span>
              <span className="text-sm text-foreground">مزامنة تلقائية عند الفتح</span>
            </div>
            <SettingsToggle enabled={autoSync} onToggle={setAutoSync} />
          </div>

          <div className="flex items-center justify-between py-2.5 border-b border-border">
            <div className="flex items-center gap-2">
              <span className="text-sm">📶</span>
              <span className="text-sm text-foreground">فقط على Wi-Fi</span>
            </div>
            <SettingsToggle enabled={wifiOnly} onToggle={setWifiOnly} />
          </div>

          <div className="flex items-center justify-between py-2.5">
            <span className="text-sm text-foreground">فترة المزامنة</span>
            <select value={interval} onChange={e => setIntervalVal(e.target.value)}
              className="text-sm border border-border rounded-lg px-2 py-1 bg-background text-foreground">
              {['5', '10', '15', '30'].map(v => (
                <option key={v} value={v}>{v} دقيقة</option>
              ))}
            </select>
          </div>
        </div>

        {/* Cache Management */}
        <div className="bg-card border border-border rounded-2xl p-4">
          <p className="font-bold text-sm text-foreground mb-3">إدارة التخزين المؤقت</p>
          <div className="space-y-2 mb-3">
            {CACHE_ITEMS.map(item => (
              <div key={item.key} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{item.icon}</span>
                  <span className="text-sm text-foreground">{item.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{cleared[item.key] ? '0 MB' : item.size}</span>
                  {!cleared[item.key] && (
                    <button onClick={() => clearCache(item.key)} className="text-xs text-destructive font-semibold px-2 py-0.5 rounded-lg bg-red-50 hover:bg-red-100 transition-colors">
                      مسح
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button onClick={clearAll} className="w-full py-2.5 text-sm font-bold text-destructive border border-destructive/30 rounded-xl bg-red-50 hover:bg-red-100 transition-colors">
            🗑️ مسح كل الـ Cache
          </button>
        </div>

      </div>
    </div>
  );
}